import React, { useEffect, useState } from 'react';
import api from '../../services/api';

type ProgressReport = {
  summary: {
    examsAttempted: number;
    examsPassed: number;
    averageScore: number;
    completionRate: number;
    activeSessions: number;
    completedSessions: number;
  };
  trend: {
    improving: boolean;
    recentAverageScore: number;
    previousAverageScore: number;
  };
  strengths: string[];
  focusAreas: string[];
  aiInsights: string[];
};

const StudentProgress: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ProgressReport | null>(null);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        const response = await api.get<ProgressReport>('/students/progress/report');
        setReport(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to load student progress report:', err);
        setError('Failed to load progress report.');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, []);

  if (loading) {
    return <div className="p-6 text-gray-600">Loading progress report...</div>;
  }

  if (error || !report) {
    return <div className="p-6 text-red-600">{error || 'Progress report unavailable.'}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">AI Progress Report</h1>
        <p className="mt-1 text-sm text-gray-600">Performance summary and personalized recommendations.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Exams Attempted</p>
          <p className="mt-1 text-2xl font-semibold">{report.summary.examsAttempted}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Exams Passed</p>
          <p className="mt-1 text-2xl font-semibold">{report.summary.examsPassed}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Average Score</p>
          <p className="mt-1 text-2xl font-semibold">{report.summary.averageScore}%</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Completion Rate</p>
          <p className="mt-1 text-2xl font-semibold">{report.summary.completionRate}%</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Active Sessions</p>
          <p className="mt-1 text-2xl font-semibold">{report.summary.activeSessions}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Completed Sessions</p>
          <p className="mt-1 text-2xl font-semibold">{report.summary.completedSessions}</p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-lg font-medium text-gray-900">Trend</h2>
        <p className="mt-2 text-sm text-gray-700">
          {report.trend.improving ? 'Improving' : 'Needs attention'} | Recent average: {report.trend.recentAverageScore}% | Previous average: {report.trend.previousAverageScore}%
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-lg font-medium text-gray-900">Strengths</h2>
          {report.strengths.length ? (
            <ul className="mt-2 list-disc pl-5 text-sm text-gray-700">
              {report.strengths.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-600">No strengths identified yet.</p>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-lg font-medium text-gray-900">Focus Areas</h2>
          {report.focusAreas.length ? (
            <ul className="mt-2 list-disc pl-5 text-sm text-gray-700">
              {report.focusAreas.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-600">No weak areas detected currently.</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-lg font-medium text-gray-900">AI Insights</h2>
        <ul className="mt-2 list-disc pl-5 text-sm text-gray-700">
          {report.aiInsights.map((insight, index) => (
            <li key={`${insight}-${index}`}>{insight}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default StudentProgress;
